import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Crown, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Pricing() {
  const { user } = useAuth();
  const [openFAQ, setOpenFAQ] = useState(null);

const handleUpgrade = async () => {
  try {
    // 1. Create order from backend (₹499 = 49900 paise if you want monthly)
    const orderRes = await axios.post(
      `${API}/subscription/create-order`,
      { amount: 100 }, // 1 INR in paise
      { withCredentials: true }
    );

    const order = orderRes.data;

    // 2. Open Razorpay checkout
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Public key
      amount: order.amount,
      currency: order.currency,
      name: "Portfolio Builder",
      description: "Pro Subscription",
      order_id: order.id,
      handler: async function (response) {
  try {
    // You can still call verify (optional, webhook is the real source of truth now)
    await axios.post(
      `${API}/subscription/verify`,
      {
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
      },
      { withCredentials: true }
    );

    toast.success("🎉 Payment successful! Activating Pro...");

    // ⏳ Poll subscription status until it becomes "pro"
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/subscription/status`, {
          withCredentials: true,
        });

        if (res.data.plan === "pro") {
          toast.success("✅ Pro activated!");
          window.location.reload();
        } else {
          setTimeout(checkStatus, 1500); // try again after 1.5s
        }
      } catch (e) {
        // if request fails, try again
        setTimeout(checkStatus, 2000);
      }
    };

    checkStatus();
  } catch (err) {
    toast.error("Payment verification failed");
  }
},

      prefill: {
        email: user?.email || "",
        name: user?.name || "",
      },
      theme: {
        color: "#4F46E5",
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (error) {
    console.error(error);
    toast.error("Failed to start payment");
  }
};


  const plans = [
    {
      name: "Free",
      price: "₹0",
      period: "forever",
      features: [
        "1 Portfolio",
        "Basic Templates",
        "GitHub Integration",
        "Manual Content Entry",
        "Portfolio Watermark",
        "Public Portfolio Link",
      ],
      current: user?.subscription_plan === "free",
      cta: "Current Plan",
    },
    {
      name: "Pro",
      price: "₹1",
      period: "forever",
      features: [
        "5 Portfolios",
        "All Premium Templates",
        "AI Content Generation",
        "AI Resume Parsing",
        "GitHub Integration",
        "Live Project Link Upload",
        "Custom Colors & Themes",
        "Image Upload",
        "Sharable Public Link",
        "Resume PDF Upload",
        "No Watermark",
      ],
      highlighted: true,
      current: user?.subscription_plan === "pro",
      cta: user?.subscription_plan === "pro" ? "Current Plan" : "Upgrade to Pro",
    },
  ];

  const faqs = [
  {
    q: "What is PortfolioAI?",
    a: "PortfolioAI is an AI-powered portfolio builder that helps developers create professional portfolios in minutes. Just add your details, choose a template, and publish instantly."
  },
  {
    q: "Do I need coding knowledge to build my portfolio?",
    a: "No. PortfolioAI is designed for everyone. You simply fill in your information, and our AI organizes it into a beautiful portfolio automatically."
  },
  {
    q: "Can I edit my portfolio after publishing?",
    a: "Yes. You can edit your portfolio anytime from your dashboard. Changes are instantly reflected on your public portfolio link."
  },
  {
    q: "What extra features do I get with Pro?",
    a: "Pro unlocks AI-powered content generation, premium templates, resume parsing, custom themes, image uploads, and up to 5 portfolios."
  },
  {
    q: "Can I connect my GitHub projects?",
    a: "Yes. PortfolioAI allows you to link your GitHub projects so they automatically appear in your portfolio."
  },
  {
    q: "Will my portfolio have a public link?",
    a: "Yes. Every portfolio gets a unique shareable link that you can send to recruiters or add to your resume and LinkedIn."
  },
  {
    q: "Can I upgrade later?",
    a: "Absolutely. You can upgrade to Pro anytime from the pricing page to unlock advanced features."
  },
  {
  q: "Is my data secure?",
  a: "Yes. Your data is securely stored and only accessible by you. PortfolioAI never shares your personal information with third parties."
}
];

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      {/* Background gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.18),transparent_60%)]" />

      {/* Navbar */}
       <nav className="relative z-20 fixed top-0 left-0 right-0 backdrop-blur-md bg-slate-950/70 border-b border-white/5">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">

    {/* Logo */}
    <Link
      to="/"
      className="flex items-center gap-2 text-white font-bold text-lg sm:text-xl truncate"
    >
      <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-sky-400" />
      <span className="truncate" style={{ fontFamily: "Outfit" }}>
        PortfolioAI
      </span>
    </Link>

    {/* Right Side */}
    <div className="flex items-center gap-2 sm:gap-4">

      {user ? (
        <Link to="/dashboard">
          <Button
            variant="outline"
            className="border-slate-700/60 text-slate-200 bg-white/5 backdrop-blur hover:bg-white/10 hover:border-sky-400/50 hover:text-white transition-all duration-300 hover:shadow-[0_0_25px_rgba(56,189,248,0.35)]"
          >
            <span className="hidden sm:inline">Dashboard</span>
            <span className="sm:hidden">📊</span>
          </Button>
        </Link>
      ) : (
        <>
          <Link to="/login">
            <Button
              variant="ghost"
              className="text-slate-400 hover:text-slate-200 hover:bg-slate-800/40 transition-colors"
            >
              <span className="hidden sm:inline">Login</span>
              <span className="sm:hidden">🔑</span>
            </Button>
          </Link>

          <Link to="/signup">
            <Button className="bg-sky-500 hover:bg-sky-400 text-black font-semibold">
              <span className="hidden sm:inline">Get Started</span>
              <span className="sm:hidden">Start</span>
            </Button>
          </Link>
        </>
      )}

    </div>
  </div>
</nav>

      {/* Pricing Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-24">
        {/* Header */}
        <div className="text-center mb-16">
          <h1
            className="text-4xl md:text-6xl font-extrabold tracking-tight text-white mb-4"
            style={{ fontFamily: "Outfit" }}
          >
            Simple, Transparent Pricing
          </h1>
          <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto">
            Start free and upgrade when you're ready to unlock AI-powered
            features.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl bg-slate-900/40 p-8 backdrop-blur transition ${
                plan.highlighted
                  ? "ring-2 ring-sky-500/60 shadow-[0_0_40px_rgba(56,189,248,0.25)]"
                  : "hover:bg-slate-900/60"
              }`}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
            >
              {/* Badge */}
              {plan.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-sky-500 text-black px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Header */}
              <div className="text-center mb-8">
                <h2
                  className="text-2xl md:text-3xl font-bold text-white mb-2"
                  style={{ fontFamily: "Outfit" }}
                >
                  {plan.name}
                </h2>
                <div className="mb-4">
                  <span className="text-4xl font-extrabold text-white">
                    {plan.price}
                  </span>
                  <span className="text-slate-400">/{plan.period}</span>
                </div>

                {/* CTA */}
                {/* CTA */}
{plan.name === "Free" ? (
  !user ? (
    <Link to="/signup">
      <Button
        variant="outline"
        className="w-full border-slate-700 text-white hover:bg-slate-800"
      >
        Get Started Free
      </Button>
    </Link>
  ) : user.subscription_plan === "free" ? (
    <Button
      variant="outline"
      disabled
      className="w-full border-slate-700 text-slate-400"
    >
      Current Plan
    </Button>
  ) : (
    <Button
      variant="outline"
      disabled
      className="w-full border-slate-700 text-slate-500"
    >
      Included in Pro
    </Button>
  )
) : plan.name === "Pro" ? (
  !user ? (
    <Link to="/login">
      <Button className="w-full bg-sky-500 hover:bg-sky-400 text-black font-semibold">
        Upgrade to Pro
      </Button>
    </Link>
  ) : user.subscription_plan === "pro" ? (
    <Button
      disabled
      className="w-full border-slate-700 text-slate-400"
    >
      Current Plan
    </Button>
  ) : (
    <Button
      onClick={handleUpgrade}
      className="w-full bg-sky-500 hover:bg-sky-400 text-black font-semibold"
    >
      Upgrade to Pro
    </Button>
  )
) : null}
              </div>

              {/* Features */}
              <ul className="space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-sky-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* FAQ SECTION */}
<div className="max-w-4xl mx-auto mt-28">
  
  <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
    Frequently Asked Questions
  </h2>

  <div className="space-y-6">
    {faqs.map((faq, i) => (
      <div
        key={i}
        className="bg-slate-900/40 border border-white/10 rounded-xl p-6 cursor-pointer transition hover:bg-slate-900/60"
        onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
      >
        <h3 className="text-lg font-semibold text-white mb-2">
          {faq.q}
        </h3>

        {openFAQ === i && (
          <p className="text-slate-400 text-sm">
            {faq.a}
          </p>
        )}
      </div>
    ))}
  </div>

</div>

        {/* Footer note */}
        <div className="mt-20 text-center">
          <p className="text-slate-400">
            Have questions?{" "}
            <a
              href="mailto:support@example.com"
              className="text-sky-400 hover:underline"
            >
              Contact our support team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
